import { CLASS_NAME } from 'constants/class-names'
import { EventType } from 'constants/enums'
import { getOuterHeight, getPageType } from 'utils/dom'
import { isPlaylistJw } from 'utils/string'
import { IVideoPlaylistItem } from './video-playlist'
import { JWPlayer } from '../player/jw-player'
import { YouTubePlayer } from '../player/yt-player'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import {
    VideoEventMap,
    YTPlayerConfigEvent,
    JWPlayerConfigEvent
} from '../types'
import { PlaylistStore } from './store'
import { AbstractPlayer } from '../player/abstract-player'
import { logger } from 'utils/system/logger'
import {
    epochToISO,
    renderUIISODuration,
    renderUITimeAgo,
    secondsToDurationISO
} from 'utils/time'
import { Sticky } from '../sticky-video.component'

const {
    common: commonClass,
    videoPlaylist: videoPlaylistClass,
    videoPlaylist: { leadvideo: leadvideoClass }
} = CLASS_NAME

const ENSURE_DOM_TIMEOUT_MS = 100
const TRIGGER_ELEMENT_RETRY_DELAY_MS = 1000

export class LeadVideoComponent extends ComponentBase {
    public player: AbstractPlayer
    protected autoplay = false
    protected mute = false

    // For the interactions with the first loaded lead video
    private playedOnHover = false

    private leadVideo: HTMLElement
    private isSticky = false
    private stickyTriggerElement?: HTMLElement
    private stickyTriggered = false
    private checkTriggerBound?: () => void

    /**
     * Initialize
     *
     * @param {HTMLElement} playlist
     */
    public constructor(private playlist: HTMLElement) {
        super()
        const leadVideo = this.getElement(`.${leadvideoClass._}`, playlist)
        if (!leadVideo) {
            return
        }
        this.leadVideo = leadVideo
        // Check if sticky mode is enabled via data attribute
        this.isSticky = leadVideo.hasAttribute('data-sticky-video-enabled')

        // Check if autoplay is enabled via data attribute
        this.autoplay = leadVideo.hasAttribute('data-video-autoplay')

        // Check if trigger exists (on the playlist/section element, not leadVideo)
        const triggerSelectorRaw = playlist.getAttribute(
            'data-trigger-selector'
        )

        // If selector provided without # or ., assume it's an ID and add #
        const triggerSelector = triggerSelectorRaw
            ? triggerSelectorRaw.startsWith('#') ||
              triggerSelectorRaw.startsWith('.')
                ? triggerSelectorRaw
                : `#${triggerSelectorRaw}`
            : undefined

        const triggerElement = triggerSelector
            ? document.querySelector<HTMLElement>(triggerSelector)
            : undefined

        // Set up scroll-based trigger for sticky
        if (this.isSticky && triggerSelector) {
            if (triggerElement) {
                this.stickyTriggerElement = triggerElement
                this.setupStickyTrigger()
            } else {
                // Element might not be in DOM yet, retry after a delay
                setTimeout(() => {
                    const retryElement =
                        document.querySelector<HTMLElement>(triggerSelector)
                    if (retryElement) {
                        this.stickyTriggerElement = retryElement
                        this.setupStickyTrigger()
                    } else {
                        // Fallback: enable sticky immediately if element not found
                        this.stickyTriggered = true
                    }
                }, TRIGGER_ELEMENT_RETRY_DELAY_MS)
            }
        } else if (this.isSticky) {
            // No trigger selector provided, enable sticky immediately
            this.stickyTriggered = true
        }

        // Check if eager load mode (should be muted for autoplay)
        const isEagerLoad = this.playlist.hasAttribute('data-video-eager-load')
        if (this.autoplay && isEagerLoad) {
            this.mute = true
        }
        PlaylistStore.getInstance().addLeadVideo(this)
    }

    /**
     * Set up scroll listener to activate sticky after user scrolls past trigger element
     * Sticky behavior is enabled once the trigger element has been scrolled past
     */
    private setupStickyTrigger(): void {
        this.checkTriggerBound = () => {
            if (this.stickyTriggered || !this.stickyTriggerElement) {
                return
            }

            const rect = this.stickyTriggerElement.getBoundingClientRect()
            // Element is considered "scrolled past" when its bottom edge is above the viewport
            const hasScrolledPast = rect.bottom < 0

            if (hasScrolledPast) {
                this.stickyTriggered = true
                // Clean up listener once triggered
                if (this.checkTriggerBound) {
                    window.removeEventListener(
                        EventType.SCROLL,
                        this.checkTriggerBound
                    )
                }
            }
        }

        // Check immediately in case already scrolled past
        this.checkTriggerBound()

        // Listen to scroll events
        window.addEventListener(EventType.SCROLL, this.checkTriggerBound)
    }

    /**
     * Get the leading video wrapper element
     *
     * @return {Nullable<HTMLElement>}
     */
    private get wrapper(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.wrapper}`, this.playlist)
    }

    /**
     * Get the leading video section title element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get title(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.title}`, this.leadVideo)
    }

    /**
     * Get the leading video section meta data parent element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get meta(): Nullable<HTMLElement> {
        return this.getElement(`.${videoPlaylistClass.meta}`, this.leadVideo)
    }

    /**
     * Get the leading video section meta Description element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get description(): Nullable<HTMLElement> {
        return this.getElement(
            `.${videoPlaylistClass.metaDescription}`,
            this.leadVideo
        )
    }

    /**
     * Get the leading description read more element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get readMore(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.readMore}`, this.leadVideo)
    }

    /**
     * Get the leading video section meta publish time element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get pubTime(): Nullable<HTMLElement> {
        return this.getElement(
            `.${videoPlaylistClass.metaPubtime}`,
            this.leadVideo
        )
    }

    /**
     * Get the leading video section meta duration element
     * get will only work after initial render
     * @return {Nullable<HTMLElement>}
     */
    private get duration(): Nullable<HTMLElement> {
        return this.getElement(
            `.${videoPlaylistClass.metaDuration}`,
            this.leadVideo
        )
    }

    /**
     * Get the hero list to resize the video component
     *
     * @return {Nullable<HTMLElement>}
     */
    private get heroList(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.heroList}`)
    }

    /**
     * Get the queue title
     *
     * @return {Nullable<HTMLElement>}
     */
    private get queueTitle(): Nullable<HTMLHeadingElement> {
        return this.getElement<HTMLHeadingElement>(
            `.${videoPlaylistClass.title}`,
            this.playlist
        )
    }

    /**
     * Get the more video element
     *
     * @return {Nullable<HTMLElement>}
     */
    private get moreVideoLink(): Nullable<HTMLElement> {
        return this.getElement(`.${leadvideoClass.moreVideo}`, this.playlist)
    }

    /**
     * Load YouTube Iframe API
     * initialize player instance
     */
    private async initYouTubePlayer(): Promise<void> {
        const leadVideo = PlaylistStore.getInstance().playlist

        const element = document.getElementById(
            `leadVideoIFrame${leadVideo.uniqueId}`
        )
        const events: YTPlayerConfigEvent = {
            onReady: (): void => this.onReady(),
            onPlay: (): void => this.onPlay()
        }

        if (element) {
            this.player = new YouTubePlayer(element, events, true)
        }
    }

    /**
     * Load Jw script
     * initialize player instance
     */
    private loadAndInitJwPlayer(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        window.addEventListener(VideoEventMap.JW_SKIP, ((
            event: CustomEvent
        ) => {
            this.renderLeadMetadataUI(
                event.detail.title,
                event.detail.description,
                epochToISO(event.detail.pubdate),
                secondsToDurationISO(event.detail.duration)
            )
        }) as EventListener)

        const element = document.getElementById(
            `leadVideoIFrame${leadVideo.uniqueId}`
        )

        const events: JWPlayerConfigEvent = {
            onReady: () => this.onReady(),
            onPlay: () => this.onPlay()
        }

        if (element) {
            element.dataset.version = this.leadVideo.dataset.version
            element.dataset.loc = this.leadVideo.dataset.loc
            this.player = new JWPlayer(element, events, true)
        }
    }

    /**
     * handle the response from the playlist ID call
     * Build HTML and attach events to newly created elements
     * then turn off loading animation
     *
     * @return {boolean} is the playlist leadlist
     */
    public init(): boolean {
        // Preserve video-placeholder element before overwriting innerHTML (for sticky mode)
        const placeholder = this.leadVideo.querySelector('.video-placeholder')
        const placeholderHtml = placeholder ? placeholder.outerHTML : ''

        // build the lead video section with a unique id (more than one on page)
        this.leadVideo.innerHTML = placeholderHtml + this.renderLeadVideo()
        this.resizeQueueListHeight()

        isPlaylistJw(PlaylistStore.getInstance().current)
            ? this.loadAndInitJwPlayer()
            : void this.initYouTubePlayer()

        this.showHideReadMore()
        const readMore = this.readMore
        if (readMore) {
            readMore.addEventListener(EventType.CLICK, () =>
                this.onClickReadMore()
            )
        }

        // Initialize sticky component if sticky mode is enabled
        if (this.isSticky) {
            // Wait for DOM to be fully rendered before initializing Sticky
            setTimeout(() => {
                const videoContainer =
                    this.leadVideo.querySelector('#video-container')
                const toggleVideo =
                    this.leadVideo.querySelector('#toggle-video')
                const closeVideo = this.leadVideo.querySelector('#close-video')

                if (videoContainer && toggleVideo && closeVideo) {
                    // Add the data attribute so ComponentFactory can identify it (but won't auto-init since we're doing it manually)
                    this.leadVideo.setAttribute(
                        'data-sticky-video-component',
                        ''
                    )

                    // Manually initialize Sticky, passing the trigger state getter
                    new Sticky(this.leadVideo, () => this.stickyTriggered)
                }
            }, ENSURE_DOM_TIMEOUT_MS) // Increased timeout to ensure DOM is ready
        }

        // remove loading animation
        this.playlist.classList.remove(CLASS_NAME.list.listLoading)

        return true
    }

    /**
     * Handle the Event for player change
     */
    protected onPlay(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        if (this.player.index !== leadVideo.currentIndex) {
            leadVideo.currentIndex = this.player.index
            this.updateLeadVideoDetails()
        }
    }

    // Desktop hover listener on category/index pages
    private createHoverListener(): void {
        // TODO: hover play until Product makes final desicion
        // bug: https://postmediadigital.atlassian.net/browse/CHEET-1380
        const enable = false
        if (enable && this.wrapper) {
            this.wrapper.addEventListener(EventType.MOUSE_ENTER, () =>
                this.playLeadVideo()
            )
        }
    }

    /**
     * Handle the Event for player ready
     */
    public onReady(): void {
        // create play lead video on hover interaction
        this.createHoverListener()
        // queue the rest of the videos in the playlist for the API controls
        this.queueVideolist()
    }

    /**
     * Load the playlist on the player for use of playlist API functions
     * https://developers.google.com/youtube/iframe_api_reference#Playlist_Queueing_Functions
     */
    public queueVideolist(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        // 🤡 Gotcha:
        // in youtube iframe api docs type = playlist
        // in pkg @types/youtube type = player
        const playlistArgs: YT.IPlaylistSettings = {
            list: leadVideo.playlistId,
            listType: 'player',
            index: leadVideo.currentIndex
        }
        this.player.cuePlaylist(playlistArgs)
    }

    /**
     * Mute and play video
     * https://developers.google.com/youtube/iframe_api_reference#changing-the-player-volume
     * https://developers.google.com/youtube/iframe_api_reference#playing-a-video
     * on desktop only will this work / is expected to work see API note:
     * https://developers.google.com/youtube/iframe_api_reference?hl=en#Mobile_considerations
     */
    public playLeadVideo(): void {
        if (!this.playedOnHover) {
            this.player.mute = true
            this.player.play()
            this.playedOnHover = true
        }
    }

    /**
     * Update title, time ago and duration meta information on the lead video section
     * Using the currently saved playing index
     */
    private updateLeadVideoDetails(): void {
        const leadVideo = PlaylistStore.getInstance().playlist

        const newDetails: IVideoPlaylistItem =
            leadVideo.playlistData.items[leadVideo.currentIndex]

        if (!newDetails) {
            if (this.meta) {
                this.meta.classList.add(commonClass.hidden)
            }
            if (this.title) {
                this.title.classList.add(commonClass.hidden)
            }
            if (this.description) {
                this.description.classList.add(commonClass.hidden)
            }
            return
        }
        this.renderLeadMetadataUI(
            newDetails.title,
            newDetails.description,
            newDetails.publishedAt,
            newDetails.duration
        )
    }

    private renderLeadMetadataUI(
        title: string,
        description: string,
        publishedAt: string,
        duration: string
    ): void {
        if (this.meta) {
            this.meta.classList.remove(commonClass.hidden)
        }
        if (this.title) {
            this.title.classList.remove(commonClass.hidden)
            this.title.innerHTML = title
        }
        if (this.description) {
            this.description.classList.remove(commonClass.hidden)
            const descFormatted = description
                ? description.replace(/\n/g, '<br>')
                : ''
            this.description.innerHTML = descFormatted
            this.collapseDescription()
            this.showHideReadMore()
        }

        if (this.pubTime) {
            this.pubTime.innerHTML = renderUITimeAgo(publishedAt)
        }
        if (this.duration) {
            this.duration.innerHTML = renderUIISODuration(duration)
        }

        // Update sticky video title if sticky mode is enabled
        if (this.isSticky) {
            const stickyVideoTitle =
                this.leadVideo.querySelector('[data-video-title]')
            if (stickyVideoTitle) {
                stickyVideoTitle.textContent = title
            }
        }
    }

    public destroy(): void {
        if (this.player) {
            this.player.destroy()
        }
        // Clean up the sticky trigger scroll listener
        if (this.checkTriggerBound) {
            window.removeEventListener(EventType.SCROLL, this.checkTriggerBound)
            this.checkTriggerBound = undefined
        }
    }

    /**
     * Build the lead video article content for the playlist
     * Using the given unique id (data-unique-id)
     */
    private renderLeadVideo(): string {
        const leadVideo = PlaylistStore.getInstance().playlist
        const firstItem = leadVideo.playlistData.items[0]
        const dataEvtVal = (
            video_platform: string,
            autoplay: boolean,
            pageType: string
        ): string => {
            return `{"video_autoplay": "${autoplay}","video_location": "${pageType} page","video_platform": "${video_platform}",
            "control_fields": { "mparticle": {
                                "keys": {"video_autoplay":"video_autoplay", "video_location": "video_location", "video_platform": "video_platform" }
                            }}}`
        }

        const jwTemplate = `
            <div
                id="leadVideoIFrame${leadVideo.uniqueId}"
                class="${leadvideoClass.jwWrapper}"
                data-video-id="${PlaylistStore.getInstance().current}"
                data-evt-val='${dataEvtVal(
                    'JW Player',
                    this.autoplay,
                    getPageType()
                )}'
                data-provider="jwplayer"
                data-position=""
                data-controls="True"
                data-loop="False"
                data-mute="${this.mute ? 'True' : 'False'}"
                data-autoplay="${this.autoplay ? 'True' : 'False'}"
                tabindex="0"
                role="application"
                aria-label="Video player"
            ></div>
            `
        const ytTemplate = `
            <div
                id="leadVideoIFrame${leadVideo.uniqueId}"
                class="${leadvideoClass.iframe}"
                data-video-id="${PlaylistStore.getInstance().current}"
                data-evt-val='${dataEvtVal(
                    'Youtube',
                    this.autoplay,
                    getPageType()
                )}'
                data-provider="youtube"
                data-position=""
                data-controls="True"
                data-loop="False"
                data-mute="${this.mute ? 'True' : 'False'}"
                data-autoplay="${this.autoplay ? 'True' : 'False'}"
                tabindex="0"
                role="application"
                aria-label="Video player"
            ></div>
        `
        const leadPlayerTemplate = isPlaylistJw(leadVideo.playlistId)
            ? jwTemplate
            : ytTemplate

        const metaDesc = window.location.toString().includes('driving')
            ? `
                    <p class="${videoPlaylistClass.metaDescription} ${videoPlaylistClass.metaDescriptionVisibleLines}">
                        ${firstItem.description}
                    </p>
                    <button class="button ${leadvideoClass.readMore} ${commonClass.hidden}" type="button">Read More</button>
                `
            : ''

        // Sticky control buttons - matching featured-video.html structure exactly
        const stickyControls = this.isSticky
            ? `
                <header class="control-center">
                    <h5 class="control-heading">
                        <span class="control-heading--max" data-video-title>${firstItem.title}</span>
                        <span class="control-heading--min">Back to video</span>
                    </h5>
                    <div class="control-btn-set">
                        <button
                            id="toggle-video"
                            class="control-btn btn-toggle-visibility"
                            type="button"
                            aria-label="Minimize video"
                        >
                            <svg class="icon-desktop" aria-hidden="true" viewBox="0 0 16 16" height="16" width="16" xmlns="http://www.w3.org/2000/svg">
                                <line class="icon-min" x1="0" y1="8" x2="16" y2="8" />
                                <polygon class="icon-max" points="1,1, 1,15, 15,15, 15,1" />
                            </svg>
                            <svg class="icon-mobile" aria-hidden="true" viewBox="0 0 22 20" height="20" width="22" xmlns="http://www.w3.org/2000/svg">
                                <rect class="icon-min" x="1" y="1" width="16" height="16" />
                                <rect class="icon-min" x="10" y="8" width="11" height="11" />
                                <polyline class="icon-max" points=" 3,14, 11,6, 19,14" />
                            </svg>
                        </button>
                        <button
                            id="close-video"
                            class="control-btn btn-close"
                            aria-label="Close sticky video"
                            type="button"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                </header>
                `
            : ''

        const template = this.isSticky
            ? `
                <section id="video-container" class="player-wrapper" tabindex="0" role="region" aria-label="Sticky video player">
                    ${stickyControls}
                    <div class="${leadvideoClass.bg}">
                        ${leadPlayerTemplate}
                    </div>
                </section>
                <h3
                    id="leadVideoTitle${leadVideo.uniqueId}"
                    class="${leadvideoClass.title}"
                >
                    ${firstItem.title}
                </h3>
                <div class="${videoPlaylistClass.meta}">
                    ${metaDesc}
                    <p class="${videoPlaylistClass.metaPubtime}">
                        ${renderUITimeAgo(firstItem.publishedAt)}
                    </p>
                    <p class="${videoPlaylistClass.metaDuration}">
                        ${renderUIISODuration(firstItem.duration)}
                    </p>
                </div>`
            : `
                <div class="${leadvideoClass.bg}">
                    ${leadPlayerTemplate}
                </div>
                <h3
                    id="leadVideoTitle${leadVideo.uniqueId}"
                    class="${leadvideoClass.title}"
                >
                    ${firstItem.title}
                </h3>
                <div class="${videoPlaylistClass.meta}">
                    ${metaDesc}
                    <p class="${videoPlaylistClass.metaPubtime}">
                        ${renderUITimeAgo(firstItem.publishedAt)}
                    </p>
                    <p class="${videoPlaylistClass.metaDuration}">
                        ${renderUIISODuration(firstItem.duration)}
                    </p>
                </div>`
        return template
    }

    /**
     * Check if text is overflowing
     */
    protected isOverflown(element: HTMLElement): boolean {
        const overflown = element.scrollHeight > element.offsetHeight
        return overflown
    }

    /**
     * Collapse the description
     */
    protected collapseDescription(): void {
        const description = this.description
        if (description) {
            if (
                !description.classList.contains(
                    videoPlaylistClass.metaDescriptionVisibleLines
                )
            ) {
                description.classList.add(
                    videoPlaylistClass.metaDescriptionVisibleLines
                )
                const readMore = this.readMore
                if (readMore) {
                    readMore.innerHTML = 'Read More'
                }
            }
        }
    }

    /**
     * Expand or Collapse the description
     */
    protected onClickReadMore(): void {
        const description = this.description
        if (description) {
            const readMore = this.readMore
            if (
                description.classList.contains(
                    videoPlaylistClass.metaDescriptionVisibleLines
                )
            ) {
                description.classList.remove(
                    videoPlaylistClass.metaDescriptionVisibleLines
                )
                if (readMore) {
                    readMore.innerHTML = 'Read Less'
                }
            } else {
                description.classList.add(
                    videoPlaylistClass.metaDescriptionVisibleLines
                )
                if (readMore) {
                    readMore.innerHTML = 'Read More'
                }
            }
        }
    }

    /**
     * Resize list height of the description if it overflows and show Read More button
     */
    protected showHideReadMore(): void {
        if (this.description) {
            const overflown = this.isOverflown(this.description)
            const readMore = this.readMore
            if (overflown && readMore) {
                readMore.classList.remove(commonClass.hidden)
            } else if (
                readMore &&
                !readMore.classList.contains(commonClass.hidden)
            ) {
                readMore?.classList.add(commonClass.hidden)
            }
        }
    }

    /**
     * Resize list height if it needs
     */
    protected resizeQueueListHeight(): void {
        // Only for the large screen
        // eslint-disable-next-line no-magic-numbers
        if (window.screen.width < 1200) {
            return
        }

        const leadVideo = PlaylistStore.getInstance().playlist
        const queueList = leadVideo.queueList

        if (!this.heroList || !this.wrapper || !this.queueTitle) {
            return
        }

        const heightHero = getOuterHeight(this.heroList)
        const heightWrapper = getOuterHeight(this.wrapper)
        const heightTitle = getOuterHeight(this.queueTitle)
        const heightMore = this.moreVideoLink
            ? getOuterHeight(this.moreVideoLink)
            : 0

        queueList.style.height = `calc(${heightHero}px - ${heightWrapper}px - ${heightTitle}px - ${heightMore}px)`
    }

    /**
     * User clicks an item
     *
     * @param {string} playlistId
     * @param {number} index
     */
    public onClick(playlistId: string, index: number): void {
        // Another playlist clicked
        if (PlaylistStore.getInstance().current !== playlistId) {
            this.playFromNewPlaylist(playlistId, index)
        } else {
            // 2. Home/Category page - single playlist
            this.playFromSamePlaylist(index)
        }
        this.updateLeadVideoDetails()

        // Update sticky video title immediately if sticky mode is enabled
        if (this.isSticky) {
            const leadVideo = PlaylistStore.getInstance().playlist
            const newDetails = leadVideo.playlistData.items[index]
            if (newDetails) {
                const stickyVideoTitle =
                    this.leadVideo.querySelector('[data-video-title]')
                if (stickyVideoTitle) {
                    stickyVideoTitle.textContent = newDetails.title
                }
            }
        }
    }

    /**
     * User player interaction involving switching between playlists
     * Currently more that one playlist are possible on Video Centre page
     * ex: https://nationalpost.com/video-centre/
     *
     * @param {string} playlistId
     * @param {number} index
     */
    private playFromNewPlaylist(playlistId: string, index: number): void {
        PlaylistStore.getInstance().current = playlistId
        this.autoplay = true
        this.player.autoplay = this.autoplay
        if (isPlaylistJw(playlistId)) {
            logger('warn', 'JW on video centre page pending', index)
        } else {
            // YT
            // YT should be defined when this callback is available
            this.player.destroy()
            this.init()
        }
    }

    /**
     * User player interaction without switching playlists
     * Currently possible for Video Widget on category/index pages or Video Centre page
     * ex: https://nationalpost.com/
     * ex: https://nationalpost.com/category/news/
     * ex: https://nationalpost.com/video-centre/
     *
     * @param {string} playlistId
     * @param {number} index
     */
    private playFromSamePlaylist(index: number): void {
        this.player.playVideoAt(index)
    }
}
